import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { History, RefreshCw, Heart, MessageCircle, Eye, BarChart3, ExternalLink, Loader2, Film, Image as ImageIcon } from "lucide-react";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function PostingHistory({ accountKey: propKey }) {
  const outlet = useOutletContext();
  const accountKey = propKey || outlet?.accountKey;
  const [data, setData] = useState({ count: 0, posts: [], last_synced: null });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [stage, setStage] = useState("");

  const load = () =>
    api.get("/meta/history", { params: { account: accountKey } }).then(({ data }) => {
      setData(data);
      setLoading(false);
    }).catch(() => setLoading(false));

  useEffect(() => {
    if (accountKey) { setLoading(true); load(); }
  }, [accountKey]);

  const sync = async () => {
    setSyncing(true);
    setStage("Wird vorbereitet…");
    try {
      const { data: r } = await api.post("/meta/sync-history", { account_key: accountKey });
      const poll = async () => {
        const { data: job } = await api.get(`/ai/rebuild-board/jobs/${r.job_id}`);
        if (job.status === "done") {
          await load();
          toast.success(`${job.synced || 0} Beiträge synchronisiert · ${job.total || 0} im Archiv`);
          setSyncing(false);
        } else if (job.status === "error") {
          toast.error(job.error || "Synchronisierung fehlgeschlagen");
          setSyncing(false);
        } else {
          setStage(job.stage || "Wird synchronisiert…");
          setTimeout(poll, 3000);
        }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kein Instagram-Konto verbunden");
      setSyncing(false);
    }
  };

  const totalViews = data.posts.reduce((s, p) => s + (p.views || 0), 0);
  const totalReach = data.posts.reduce((s, p) => s + (p.reach || 0), 0);
  const totalEng = data.posts.reduce((s, p) => s + (p.like_count || 0) + (p.comments_count || 0), 0);

  return (
    <div data-testid="posting-history" className="max-w-6xl">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-400 flex items-center gap-1.5">
            <History className="h-3.5 w-3.5" /> Gedächtnis · Posting-Archiv
          </p>
          <h1 className="font-head text-2xl sm:text-3xl font-bold text-neutral-900 tracking-tight mt-1">Posting-History</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Alle vergangenen Instagram-Beiträge mit echten Kennzahlen — die Basis, aus der das Board lernt.
          </p>
        </div>
        <button
          data-testid="sync-history-btn"
          onClick={sync}
          disabled={syncing}
          className="inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-neutral-800 transition disabled:opacity-60 shrink-0"
        >
          {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {syncing ? stage : "Verlauf synchronisieren"}
        </button>
      </div>

      {data.count > 0 && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card label="Beiträge im Archiv" value={data.count.toLocaleString("de-DE")} icon={History} />
          <Card label="Views gesamt" value={totalViews.toLocaleString("de-DE")} icon={Eye} />
          <Card label="Reichweite gesamt" value={totalReach.toLocaleString("de-DE")} icon={BarChart3} />
          <Card label="Engagement gesamt" value={totalEng.toLocaleString("de-DE")} icon={Heart} />
        </div>
      )}

      {loading ? (
        <div className="text-neutral-400 py-16 text-center">Lade Archiv…</div>
      ) : data.count === 0 ? (
        <div className="mt-8 bg-white border border-dashed border-neutral-300 rounded-xl p-12 text-center">
          <History className="h-8 w-8 text-neutral-300 mx-auto" />
          <p className="text-neutral-600 font-medium mt-3">Noch kein Archiv vorhanden</p>
          <p className="text-sm text-neutral-400 mt-1">Klicke auf „Verlauf synchronisieren", um deine vergangenen Beiträge zu speichern.</p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.posts.map((p) => (
            <div key={p.ig_media_id} data-testid={`history-post-${p.ig_media_id}`} className="bg-white border border-neutral-200 rounded-xl p-4 flex gap-4 hover:shadow-md transition">
              <div className="h-24 w-24 shrink-0 rounded-lg bg-neutral-100 overflow-hidden relative">
                {p.thumbnail_url ? (
                  <img src={p.thumbnail_url} alt="" className="h-full w-full object-cover" loading="lazy" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-neutral-300">
                    {p.media_product_type === "REELS" ? <Film /> : <ImageIcon />}
                  </div>
                )}
                <span className="absolute top-1 left-1 text-[9px] font-bold uppercase bg-black/60 text-white px-1.5 py-0.5 rounded">
                  {p.media_product_type === "REELS" ? "Reel" : p.media_type === "VIDEO" ? "Video" : "Post"}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-neutral-400">{p.timestamp ? format(parseISO(p.timestamp), "d. MMM yyyy", { locale: de }) : "—"}</span>
                  {p.permalink && (
                    <a href={p.permalink} target="_blank" rel="noreferrer" className="text-neutral-300 hover:text-neutral-600">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
                <p className="text-[13px] text-neutral-700 line-clamp-2 mt-1">{(p.caption || "—").replace(/\n/g, " ")}</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-neutral-500 flex-wrap">
                  <span className="flex items-center gap-1"><Heart className="h-3.5 w-3.5 text-rose-400" /> {(p.like_count || 0).toLocaleString("de-DE")}</span>
                  <span className="flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5 text-blue-400" /> {(p.comments_count || 0).toLocaleString("de-DE")}</span>
                  {p.views != null && <span className="flex items-center gap-1"><Eye className="h-3.5 w-3.5 text-fuchsia-400" /> {p.views.toLocaleString("de-DE")}</span>}
                  {p.reach != null && <span className="flex items-center gap-1"><BarChart3 className="h-3.5 w-3.5 text-teal-400" /> {p.reach.toLocaleString("de-DE")}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Card({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4">
      <div className="flex items-center gap-2 text-neutral-400">
        <Icon className="h-4 w-4" />
        <span className="text-[11px] uppercase tracking-wider">{label}</span>
      </div>
      <div className="font-head text-2xl font-bold text-neutral-900 mt-1">{value}</div>
    </div>
  );
}
